import { FormEvent, useCallback, useEffect, useState } from 'react';
import type {
  AdminBackupsResponse,
  AuthTotpStatusResponse,
  ErrorLog,
} from '../../shared/types';
import {
  AdminFooter,
  AdminShell,
  FieldLabel,
  SectionHeading,
  StatePanel,
  cardClass,
  formatBytes,
  formatDateTime,
  inputClass,
  quietButtonClass,
  secondaryButtonClass,
} from '../components/admin/AdminUi';
import CopyButton from '../components/admin/CopyButton';
import { showToast } from '../components/Toaster';
import { adminApi, ApiError, authApi } from '../lib/api';

export default function AdminSecurityPage() {
  return (
    <AdminShell>
      <div className="mx-auto max-w-5xl px-5 py-10 md:px-10 md:py-14">
        <header className="rise">
          <SectionHeading
            level="page"
            eyebrow="Admin"
            title="Security"
            description="Two-factor, backups and the error log, in one place."
          />
        </header>

        <main className="mt-6 grid gap-6">
          <TotpPanel />
          <BackupsPanel />
          <ProductionErrorsPanel />
        </main>
      </div>
      <AdminFooter path="ss-review / security" />
    </AdminShell>
  );
}

function TotpPanel() {
  const [status, setStatus] = useState<AuthTotpStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const nextStatus = await authApi.totpStatus();
      setStatus(nextStatus);
    } catch (caught) {
      setLoadError(
        caught instanceof ApiError
          ? caught.message
          : 'We could not check two-factor authentication. Mind trying again?',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function setup() {
    setBusy(true);
    setActionError(null);
    try {
      const response = await authApi.totpSetup();
      setSecret(response.secret);
      setStatus({ enabled: false, pending: true });
      setCode('');
    } catch (caught) {
      setActionError(apiMessage(caught, 'We could not start two-factor setup. Mind trying again?'));
    } finally {
      setBusy(false);
    }
  }

  async function enable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await authApi.totpEnable({ code });
      setSecret(null);
      setCode('');
      await load();
      showToast('success', 'Two-factor is on.');
    } catch (caught) {
      setActionError(apiMessage(caught, 'We could not turn on two-factor. Mind trying again?'));
    } finally {
      setBusy(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await authApi.totpDisable({ password, code });
      setPassword('');
      setCode('');
      await load();
      showToast('success', 'Two-factor is off.');
    } catch (caught) {
      setActionError(apiMessage(caught, 'We could not turn off two-factor. Mind trying again?'));
    } finally {
      setBusy(false);
    }
  }

  if (status === null && loadError === null) {
    return (
      <StatePanel eyebrow="Loading" title="Checking two-factor authentication">
        <p>We are checking the admin account now.</p>
      </StatePanel>
    );
  }

  if (loadError) {
    return (
      <StatePanel eyebrow="Could not load" title="Two-factor is still unchanged" tone="error">
        <p>{loadError}</p>
        <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void load()}>
          Try again
        </button>
      </StatePanel>
    );
  }

  const readyStatus = status!;
  const isSetup = readyStatus.pending && !readyStatus.enabled;

  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-7`} aria-labelledby="totp-title">
      <SectionHeading
        eyebrow="Two-factor authentication"
        title={<span id="totp-title">Add a second lock to the admin door</span>}
      />

      {readyStatus.enabled ? (
        <>
          <p className="mt-6 flex items-start gap-3 text-sm font-semibold text-success">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-success" aria-hidden="true" />
            On — your admin login now needs a code from your authenticator app.
          </p>
          <p className="mt-3 text-sm text-muted">
            Signing in from the Studio OS doesn't ask for a code — it trusts the OS sign-in.
          </p>
          <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={disable}>
            <label>
              <FieldLabel>Password</FieldLabel>
              <input
                className={inputClass}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={busy}
              />
            </label>
            <label>
              <FieldLabel>Current 6-digit code</FieldLabel>
              <TotpCodeInput value={code} onChange={setCode} disabled={busy} />
            </label>
            <div className="sm:col-span-2">
              <button type="submit" className={secondaryButtonClass} disabled={busy || code.length !== 6}>
                {busy ? 'Turning off two-factor' : 'Turn off two-factor'}
              </button>
            </div>
          </form>
        </>
      ) : isSetup && secret ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-input border border-line bg-elevated px-4 py-3">
            <code className="break-all font-mono text-sm tracking-[0.08em] text-fg">{secret}</code>
            <CopyButton
              text={secret}
              label="Copy secret"
              manualLabel="Two-factor secret to copy manually"
              successMessage="Secret copied."
              className={quietButtonClass}
            />
          </div>
          <p className="mt-4 max-w-2xl text-sm text-muted">
            Add this to your authenticator app, then enter the first code it shows to switch two-factor on.
          </p>
          <form className="mt-5 flex flex-wrap items-end gap-3" onSubmit={enable}>
            <label className="w-full max-w-xs">
              <FieldLabel>6-digit code</FieldLabel>
              <TotpCodeInput value={code} onChange={setCode} disabled={busy} />
            </label>
            <button type="submit" className={secondaryButtonClass} disabled={busy || code.length !== 6}>
              {busy ? 'Turning on two-factor' : 'Turn on two-factor'}
            </button>
          </form>
        </>
      ) : (
        <>
          <p className="mt-6 flex items-start gap-3 text-sm text-muted">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-faint" aria-hidden="true" />
            {isSetup
              ? 'Setup is waiting for a code. Generate a new secret to continue.'
              : 'Off — your password is the only thing guarding this admin right now.'}
          </p>
          <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void setup()} disabled={busy}>
            {busy
              ? 'Starting setup'
              : isSetup
                ? 'Generate a new secret'
                : 'Set up two-factor'}
          </button>
        </>
      )}

      {actionError ? (
        <p className="mt-5 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">
          {actionError}
        </p>
      ) : null}

      <p className="mt-7 border-t border-line pt-5 text-xs leading-relaxed text-faint">
        If you are locked out, an operator with direct D1 access must clear{' '}
        <code className="font-mono text-muted">totp_secret_b32</code> and{' '}
        <code className="font-mono text-muted">totp_enabled_at</code> on the{' '}
        <code className="font-mono text-muted">admin</code> table.
      </p>
    </section>
  );
}

function TotpCodeInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <input
      className={`${inputClass} font-mono tracking-[0.2em]`}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      placeholder="000 000"
      value={value}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
      required
      disabled={disabled}
    />
  );
}

function BackupsPanel() {
  const [backups, setBackups] = useState<AdminBackupsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setBackups(await adminApi.backups());
    } catch (caught) {
      setLoadError(apiMessage(caught, 'We could not check the latest backup. Mind trying again?'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runBackup() {
    setRunning(true);
    setRunError(null);
    try {
      await adminApi.runBackup();
      showToast('success', 'Backup complete.');
      await load();
    } catch (caught) {
      setRunError(apiMessage(caught, 'We could not run the backup. Mind trying again?'));
    } finally {
      setRunning(false);
    }
  }

  if (backups === null && loadError === null) {
    return (
      <StatePanel eyebrow="Loading" title="Checking database backups">
        <p>We are looking for the newest weekly snapshot now.</p>
      </StatePanel>
    );
  }

  if (loadError) {
    return (
      <StatePanel eyebrow="Could not load" title="The backup schedule is still in place" tone="error">
        <p>{loadError}</p>
        <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void load()}>
          Try again
        </button>
      </StatePanel>
    );
  }

  const lastBackup = backups!.last_backup;
  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-7`} aria-labelledby="backups-title">
      <SectionHeading
        eyebrow="Database backups"
        title={<span id="backups-title">Weekly snapshots, kept safe in R2</span>}
        description="A snapshot runs every Sunday at 3:00 am Melbourne time. The last eight are kept; older ones roll off automatically."
      />
      <p className="mt-6 flex items-start gap-3 text-sm text-muted">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${lastBackup ? 'bg-success' : 'bg-faint'}`} aria-hidden="true" />
        {lastBackup
          ? `Last backup: ${formatDateTime(lastBackup.timestamp)} · ${formatBytes(lastBackup.size)} · ${lastBackup.kind === 'weekly' ? 'Weekly' : 'Manual'}`
          : 'Last backup: No snapshots yet.'}
      </p>
      <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void runBackup()} disabled={running}>
        {running ? 'Running backup' : 'Run backup now'}
      </button>
      {runError ? (
        <p className="mt-5 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">
          {runError}
        </p>
      ) : null}
    </section>
  );
}

function ProductionErrorsPanel() {
  const [errors, setErrors] = useState<ErrorLog[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    setMessage(null);
    try {
      setErrors((await authApi.errors()).errors);
    } catch (caught) {
      setMessage(
        caught instanceof ApiError
          ? caught.message
          : 'We could not load the error log. Mind trying again?',
      );
      setErrors([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-7`} aria-labelledby="errors-title">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeading
          eyebrow="Production errors"
          title={<span id="errors-title">Recent errors</span>}
          description="The newest server-side failures recorded by the Worker. Entries purge after 30 days."
        />
        <button
          type="button"
          className={quietButtonClass}
          onClick={() => void load()}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing' : 'Refresh'}
        </button>
      </div>

      <div className="mt-6" aria-live="polite">
        {errors === null ? (
          <StatePanel eyebrow="Loading" title="Checking the error log">
            <p>We are gathering the most recent entries now.</p>
          </StatePanel>
        ) : message ? (
          <StatePanel eyebrow="Could not load" title="The log is still safe" tone="error">
            <p>{message}</p>
          </StatePanel>
        ) : errors.length === 0 ? (
          <StatePanel eyebrow="All clear" title="No errors have been recorded">
            <p>Quiet is good. Refresh this page if you are investigating something recent.</p>
          </StatePanel>
        ) : (
          <ul className="grid gap-3">
            {errors.map((error) => <ErrorEntry key={error.id} error={error} />)}
          </ul>
        )}
      </div>
    </section>
  );
}

function ErrorEntry({ error }: { error: ErrorLog }) {
  return (
    <li className={`${cardClass} rise-subtle overflow-hidden`}>
      <details>
        <summary className="min-h-[56px] cursor-pointer list-none px-5 py-4 marker:content-none md:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-accent">
                {error.source}
              </p>
              <p className="mt-2 break-words text-sm text-fg">{error.message}</p>
            </div>
            <time className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              {formatTimestamp(error.created_at)}
            </time>
          </div>
        </summary>
        {error.context || error.stack ? (
          <div className="grid gap-4 border-t border-line bg-elevated px-5 py-4 md:px-6">
            {error.context ? <ErrorDetail label="Context" value={error.context} /> : null}
            {error.stack ? <ErrorDetail label="Stack" value={error.stack} /> : null}
          </div>
        ) : null}
      </details>
    </li>
  );
}

function ErrorDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">{label}</p>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted">
        {value}
      </pre>
    </div>
  );
}

function formatTimestamp(value: number): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value);
}

function apiMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}
